import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface BusinessSummary {
  id: string;
  name: string;
  slug: string;
  booking_settings?: {
    logo_url?: string;
    logo_invert?: boolean;
  };
}

interface QueueStatusRow {
  booking_id: string;
  business_id: string;
  queue_position: number | null;
  queue_status: string | null;
  stage_id: string | null;
  stage_title: string | null;
  stage_order: number | null;
  stage_status: string | null;
  is_completed: boolean | null;
  updated_at: string | null;
}

const POLL_INTERVAL_MS = 5000;

export default function PublicQueueStatus() {
  const { businessSlug, token } = useParams<{ businessSlug: string; token: string }>();
  
  const [business, setBusiness] = useState<BusinessSummary | null>(null);
  const [statusRow, setStatusRow] = useState<QueueStatusRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Design settings (same as Thank You page)
  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerColor, setHeaderColor] = useState('#111827');
  const [displayLogo, setDisplayLogo] = useState(false);
  const [displayCompanyName, setDisplayCompanyName] = useState(false);

  // Fetch business info and design settings
  useEffect(() => {
    if (!businessSlug) {
      setError('Invalid business URL');
      setIsLoading(false);
      return;
    }

    const fetchBusinessAndSettings = async () => {
      try {
        // Fetch business
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('id, name, slug, booking_settings')
          .eq('slug', businessSlug)
          .single();

        if (businessError) {
          console.error('Business fetch error:', businessError);
          setError('Unable to load business information');
          setIsLoading(false);
          return;
        }

        setBusiness(businessData);

        // Fetch design settings
        if (businessData?.id) {
          const { data: settings } = await supabase
            .from('que_settings')
            .select('header_enabled, header_color, display_logo, display_company_name')
            .eq('business_id', businessData.id)
            .maybeSingle();

          if (settings) {
            setHeaderEnabled(settings.header_enabled || false);
            setHeaderColor(settings.header_color || '#111827');
            setDisplayLogo(settings.display_logo || false);
            setDisplayCompanyName(settings.display_company_name || false);
          }
        }
      } catch (err) {
        console.error('Error loading business:', err);
        setError('Unable to load business information');
        setIsLoading(false);
      }
    };

    fetchBusinessAndSettings();
  }, [businessSlug]);

  // Fetch queue status
  const fetchStatus = useCallback(async () => {
    if (!token) {
      setError('Invalid link - missing token');
      setIsLoading(false);
      return;
    }

    try {
      const { data, error: statusError } = await supabase.rpc('get_public_queue_status', {
        p_token: token
      });

      if (statusError) {
        console.error('Status fetch error:', statusError);
        setError('Unable to load queue status');
        setStatusRow(null);
        return;
      }

      const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
      
      if (!row || !row.booking_id) {
        setError('This queue link is no longer valid.');
        setStatusRow(null);
      } else {
        setStatusRow(row);
        setError(null);
      }
    } catch (err) {
      console.error('Error loading queue status:', err);
      setError('Unable to load queue status');
      setStatusRow(null);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Poll for status updates
  useEffect(() => {
    if (!token) return;

    fetchStatus();
    const interval = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [token, fetchStatus]);

  // Compute display status
  const primaryStatus = useMemo(() => {
    if (!statusRow) return null;
    
    // Priority 1: Still in the queue
    if (statusRow.queue_position !== null && statusRow.queue_position > 0) {
      return {
        type: 'queue' as const,
        title: `Position #${statusRow.queue_position}`,
        subtitle: 'You are in the queue. We\'ll get to you soon!'
      };
    }
    
    // Priority 2: In an active handoff stage
    if (statusRow.stage_id && statusRow.stage_status === 'active') {
      return {
        type: 'stage' as const,
        title: 'We are currently working on your...',
        stageName: statusRow.stage_title || 'Service'
      };
    }
    
    // Priority 3: All done
    if (statusRow.is_completed) {
      return {
        type: 'completed' as const,
        title: 'All Done!',
        subtitle: 'Your service has been completed. Thank you!'
      };
    }
    
    // Fallback
    return {
      type: 'processing' as const,
      title: 'Processing',
      subtitle: 'Your request is being processed'
    };
  }, [statusRow]);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header - Same as Thank You page */}
      {headerEnabled && (
        <div style={{
          height: '96px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 24px',
          backgroundColor: headerColor
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {displayLogo && business?.booking_settings?.logo_url && (
              <img
                src={business.booking_settings.logo_url}
                alt="Business logo"
                style={{ height: '64px', width: '64px', objectFit: 'contain' }}
              />
            )}
            {displayCompanyName && business?.name && (
              <h1 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'white',
                margin: 0
              }}>
                {business.name}
              </h1>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        {/* Main Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          {/* Loading State */}
          {isLoading && (
            <div>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #e5e7eb',
                borderTopColor: '#3b82f6',
                borderRadius: '50%',
                margin: '0 auto 16px',
                animation: 'spin 1s linear infinite'
              }} />
              <p style={{ color: '#6b7280', fontSize: '16px', margin: 0 }}>Loading your queue status...</p>
            </div>
          )}

          {/* Error State */}
          {!isLoading && error && (
            <div>
              <div style={{
                width: '48px',
                height: '48px',
                backgroundColor: '#fef2f2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '24px'
              }}>
                ⚠️
              </div>
              <p style={{ color: '#dc2626', fontSize: '16px', fontWeight: '500', margin: 0 }}>{error}</p>
            </div>
          )}

          {/* Success State */}
          {!isLoading && !error && primaryStatus && (
            <div>
              {/* Queue Position - Blue circle with number */}
              {primaryStatus.type === 'queue' && statusRow?.queue_position && (
                <div style={{
                  width: '120px',
                  height: '120px',
                  backgroundColor: '#3b82f6',
                  borderRadius: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  color: 'white',
                  boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5)'
                }}>
                  <span style={{ fontSize: '42px', fontWeight: 'bold', lineHeight: 1 }}>
                    {statusRow.queue_position}
                  </span>
                  <span style={{ fontSize: '12px', opacity: 0.9, marginTop: '4px' }}>
                    in queue
                  </span>
                </div>
              )}

              {/* Active Stage - Blue pulsing indicator */}
              {primaryStatus.type === 'stage' && (
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#3b82f6',
                  borderRadius: '50%',
                  margin: '0 auto 24px',
                  boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5)',
                  animation: 'pulse 2s ease-in-out infinite'
                }} />
              )}

              {/* Completed - Green checkmark */}
              {primaryStatus.type === 'completed' && (
                <div style={{
                  width: '120px',
                  height: '120px',
                  backgroundColor: '#10b981',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  color: 'white',
                  fontSize: '56px',
                  boxShadow: '0 10px 25px -5px rgba(16, 185, 129, 0.5)'
                }}>
                  ✓
                </div>
              )}

              {/* Processing - Gray */}
              {primaryStatus.type === 'processing' && (
                <div style={{
                  width: '100px',
                  height: '100px',
                  backgroundColor: '#6b7280',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  color: 'white',
                  fontSize: '40px'
                }}>
                  ⏳
                </div>
              )}

              <h1 style={{
                fontSize: '22px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '8px',
                margin: '0 0 8px 0'
              }}>
                {primaryStatus.title}
              </h1>
              
              {/* Stage name - Blue color */}
              {'stageName' in primaryStatus && primaryStatus.stageName && (
                <p style={{
                  fontSize: '28px',
                  fontWeight: 'bold',
                  color: '#3b82f6',
                  margin: '0 0 8px 0'
                }}>
                  {primaryStatus.stageName}
                </p>
              )}
              
              {/* Subtitle */}
              {'subtitle' in primaryStatus && primaryStatus.subtitle && (
                <p style={{
                  fontSize: '16px',
                  color: '#6b7280',
                  maxWidth: '280px',
                  margin: '0 auto'
                }}>
                  {primaryStatus.subtitle}
                </p>
              )}

              {/* Auto-refresh notice */}
              <p style={{
                fontSize: '12px',
                color: '#9ca3af',
                marginTop: '32px',
                marginBottom: 0
              }}>
                This page updates automatically
              </p>
            </div>
          )}
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}
